import { useState } from 'react';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PurchaseRequestDetailSchema, type PurchaseRequestDetailDto } from '@taqon/contracts';
import { Link as RouterLink, useParams } from 'react-router';
import { errorMessage, getData, postJson } from '../../shared/api/client';
import { RecordHistory } from '../../shared/audit/AuditTrail';
import { DocumentsPanel } from '../../shared/documents/DocumentsPanel';
import { hasCapability, useSession } from '../../shared/session/session';
import { ConfirmDialog } from '../../shared/ui/confirm';
import { StatusChip } from '../../shared/ui/forms';
import { EmptyState, LoadingState, PageHeader, Section } from '../../shared/ui/page';
import { PersonName } from '../../shared/ui/person';
import { ApprovalPanel, useSubmittedByMe } from '../../shared/workflow/ApprovalPanel';
import { Facts, RecordFailure, RecordLink } from '../finance/detail';
import { useActionFeedback } from '../happiness/ActionFeedback';
import {
  dimensionText,
  EDITABLE,
  PO_TYPE,
  PR_API,
  PR_TYPE,
  PurchasingLink,
  useProcurementLists,
} from './purchasing';

type Action = 'submit' | 'withdraw' | 'cancel';

const ACTION_TEXT: Readonly<Record<Action, { title: string; message: string; label: string }>> = {
  submit: {
    title: 'Submit for approval',
    message:
      'The request goes to its configured approval route and cannot be edited while in approval.',
    label: 'Submit',
  },
  withdraw: {
    title: 'Withdraw from approval',
    message:
      'The request leaves approval and goes back to draft. Its approvers are no longer asked to ' +
      'decide it; you can correct and resubmit it, or cancel it.',
    label: 'Withdraw',
  },
  cancel: {
    title: 'Cancel the purchase request',
    message: 'A cancelled request cannot be submitted or converted into a purchase order.',
    label: 'Cancel request',
  },
};

/**
 * One purchase request (`/procurement/purchase-requests/:id`; FRD-PRC-017 to 026): the request,
 * its lines with what is already on purchase orders, the orders converting it, its approvals,
 * attachments and history. Actions follow the status and the session's permissions; the API
 * decides regardless.
 */
export function PurchaseRequestDetailPage() {
  const { id = '' } = useParams();
  const session = useSession();
  const queryClient = useQueryClient();
  const can = (capability: string) => hasCapability(session.data, capability);
  const [confirming, setConfirming] = useState<Action | null>(null);
  const request = useQuery({
    queryKey: ['procurement', 'purchase-request', id],
    queryFn: () => getData(`${PR_API}/${id}`, PurchaseRequestDetailSchema),
  });
  const feedback = useActionFeedback();
  const lists = useProcurementLists();
  const submittedByMe = useSubmittedByMe(PR_TYPE, id, request.data?.request.status === 'submitted');
  const act = useMutation({
    mutationFn: (action: Action) =>
      postJson(
        `${PR_API}/${id}/${action}`,
        {},
        PurchaseRequestDetailSchema,
        session.data?.csrfToken ?? '',
        { expectedVersion: request.data?.request.version },
      ),
    onSuccess: async (_saved, action) => {
      setConfirming(null);
      // Just submitted: offer to rate how it went (FRD-CORE-062), once a day.
      if (action === 'submit') feedback.offer('purchase_request');
      await queryClient.invalidateQueries({ queryKey: ['procurement'] });
      await queryClient.invalidateQueries({ queryKey: ['workflow'] });
    },
  });
  const back = (
    <Button
      component={RouterLink}
      to="/procurement/purchase-requests"
      startIcon={<ArrowBackIcon />}
    >
      All purchase requests
    </Button>
  );
  if (request.isPending) return <LoadingState label="Loading the purchase request" />;
  if (request.isError) {
    return (
      <RecordFailure
        title="Purchase request"
        noun="purchase request"
        error={request.error}
        back={back}
      />
    );
  }
  const detail: PurchaseRequestDetailDto = request.data;
  const r = detail.request;
  const canChange = can(`${PR_TYPE}:change`);
  const editable = EDITABLE.includes(r.status);
  const category = (code: string | null) =>
    code === null ? '—' : (lists.data?.prCategory.find((v) => v.valueCode === code)?.label ?? code);

  return (
    <Stack spacing={3}>
      <PageHeader
        title={`Purchase request ${r.documentNo}`}
        description={r.justification}
        actions={
          <>
            {back}
            {canChange && editable && (
              <Button component={RouterLink} to={`/procurement/purchase-requests/${r.id}/edit`}>
                Edit
              </Button>
            )}
            {canChange && editable && (
              <Button
                variant="contained"
                onClick={() => {
                  setConfirming('submit');
                }}
              >
                {r.status === 'draft' ? 'Submit' : 'Resubmit'}
              </Button>
            )}
            {canChange && submittedByMe && (
              <Button
                onClick={() => {
                  setConfirming('withdraw');
                }}
              >
                Withdraw
              </Button>
            )}
            {canChange && editable && (
              <Button
                color="warning"
                onClick={() => {
                  setConfirming('cancel');
                }}
              >
                Cancel request
              </Button>
            )}
            {can(`${PO_TYPE}:create`) && r.status === 'approved' && (
              <Button
                variant="contained"
                component={RouterLink}
                to={`/procurement/purchase-orders/new?request=${r.id}`}
              >
                Create purchase order
              </Button>
            )}
          </>
        }
      />
      <Section title="Request">
        <Facts
          label="Purchase request details"
          facts={[
            ['Status', <StatusChip key="s" status={r.status} />],
            [
              'Requester',
              r.requesterLabel == null ? (
                '—'
              ) : (
                <PersonName key="req" name={r.requesterLabel} employeeId={r.requesterEmployeeId} />
              ),
            ],
            ['Department', detail.departmentLabel ?? '—'],
            ['Cost centre', detail.costCenterLabel ?? '—'],
            ['Request date', r.requestDate],
            [
              'Preferred supplier',
              r.preferredSupplierId === null ? (
                '—'
              ) : (
                <PurchasingLink key="sup" type="procurement.supplier" id={r.preferredSupplierId}>
                  {r.preferredSupplierLabel ?? r.preferredSupplierId}
                </PurchasingLink>
              ),
            ],
            ['Estimated total (AED)', r.estimatedTotal],
          ]}
        />
      </Section>
      <Section title="Lines">
        <Table size="small" aria-label="Purchase request lines">
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Item or service</TableCell>
              <TableCell align="right">Quantity</TableCell>
              <TableCell align="right">Unit cost</TableCell>
              <TableCell align="right">Line cost</TableCell>
              <TableCell>Required by</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Account</TableCell>
              <TableCell>Dimensions</TableCell>
              <TableCell>Asset class</TableCell>
              <TableCell>Tax code</TableCell>
              <TableCell>Budget reference</TableCell>
              <TableCell align="right">On order</TableCell>
              <TableCell align="right">Remaining</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {detail.lines.map((line) => (
              <TableRow key={line.id}>
                <TableCell>{line.lineNo}</TableCell>
                <TableCell>{line.description}</TableCell>
                <TableCell align="right">{line.quantity}</TableCell>
                <TableCell align="right">{line.estimatedUnitCost}</TableCell>
                <TableCell align="right">{line.estimatedLineCost}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{line.requiredByDate}</TableCell>
                <TableCell>{category(line.categoryCode)}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{line.accountLabel ?? '—'}</TableCell>
                <TableCell>{dimensionText(line)}</TableCell>
                <TableCell>
                  {line.isAsset ? (line.assetClassLabel ?? 'Asset line') : 'Not an asset'}
                </TableCell>
                <TableCell>{line.taxCodeLabel ?? '—'}</TableCell>
                <TableCell>{line.budgetReference ?? '—'}</TableCell>
                <TableCell align="right">{line.convertedQuantity}</TableCell>
                <TableCell align="right">{line.remainingQuantity}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Section>
      <Section title="Purchase orders">
        {detail.purchaseOrders.length === 0 ? (
          <EmptyState title="No purchase orders yet" />
        ) : (
          <Table size="small" aria-label="Purchase orders from this request">
            <TableHead>
              <TableRow>
                <TableCell>Order</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {detail.purchaseOrders.map((o) => (
                <TableRow key={o.id}>
                  <TableCell>
                    {can(`${PO_TYPE}:view`) ? (
                      <RecordLink to={`/procurement/purchase-orders/${o.id}`}>
                        {o.documentNo}
                      </RecordLink>
                    ) : (
                      o.documentNo
                    )}
                  </TableCell>
                  <TableCell>
                    <StatusChip status={o.status} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Section>
      <ApprovalPanel
        recordType={PR_TYPE}
        recordId={r.id}
        onDecided={() => {
          void queryClient.invalidateQueries({ queryKey: ['procurement'] });
        }}
      />
      <DocumentsPanel recordType={PR_TYPE} recordId={r.id} canAttach={canChange} />
      <RecordHistory recordType={PR_TYPE} recordId={r.id} />
      {feedback.dialog}
      {confirming !== null && (
        <ConfirmDialog
          open
          title={ACTION_TEXT[confirming].title}
          message={ACTION_TEXT[confirming].message}
          confirmLabel={ACTION_TEXT[confirming].label}
          tone={confirming === 'cancel' ? 'warning' : 'primary'}
          busy={act.isPending}
          error={act.isError ? errorMessage(act.error) : null}
          onCancel={() => {
            act.reset();
            setConfirming(null);
          }}
          onConfirm={() => {
            act.mutate(confirming);
          }}
        />
      )}
    </Stack>
  );
}
